import { doc, getFirestore, updateDoc } from "firebase/firestore";

type AnyObject = Record<string, any>;

export type ClienteCuotas = AnyObject & {
  uid?: string;
  abono?: number;
  fechaUltimoAbono?: string | Date;
  actualizado?: string | Date;
  formaDePago?: string;
  cuotasAtrasadas?: number;
};

export type ActualizarCuotasParams = {
  infoClientes: ClienteCuotas[] | null;
  usuarioRuta: AnyObject | null;
};

const normalizarFecha = (fecha: string | Date | undefined): number => {
  if (!fecha) {
    return Number.NEGATIVE_INFINITY;
  }

  const fechaNormalizada = new Date(fecha);
  fechaNormalizada.setHours(0, 0, 0, 0);
  return fechaNormalizada.getTime();
};

const esPagoNoDiario = (formaDePago?: string): boolean => {
  return formaDePago === "semanal" || formaDePago === "mensual";
};

const crearClienteActualizado = (
  cliente: ClienteCuotas,
  cambios: Partial<ClienteCuotas>,
): ClienteCuotas => {
  return {
    ...cliente,
    ...cambios,
  };
};

export const actualizarCuotas = async ({
  infoClientes,
  usuarioRuta,
}: ActualizarCuotasParams): Promise<ClienteCuotas[] | null> => {
  if (!infoClientes?.length || !usuarioRuta) {
    return infoClientes;
  }

  const db = getFirestore();

  const fechaActual = new Date();
  fechaActual.setHours(0, 0, 0, 0);

  const fechaActualTime = fechaActual.getTime();
  const fechaActualString = fechaActual.toDateString();
  const promesasActualizacion: Promise<any>[] = [];
  let infoClientesActualizado = infoClientes;
  let huboCambios = false;

  for (let index = 0; index < infoClientes.length; index += 1) {
    const cliente = infoClientes[index];
    const fechaUltimoAbonoTime = normalizarFecha(cliente.fechaUltimoAbono);
    const ultimaActualizacionTime = normalizarFecha(cliente.actualizado);
    const noEsHoy = fechaUltimoAbonoTime < fechaActualTime;
    const actualizadoHoy = ultimaActualizacionTime === fechaActualTime;

    if (!noEsHoy || actualizadoHoy) {
      continue;
    }

    const clienteRef = doc(
      db,
      "admin_users",
      usuarioRuta.adminUid,
      "rutas",
      usuarioRuta.uid,
      "clientes",
      cliente.uid,
    );

    if ((cliente.abono ?? 0) === 0) {
      const cuotasAtrasadasActualizadas = esPagoNoDiario(cliente.formaDePago)
        ? 0
        : Number(cliente.cuotasAtrasadas ?? 0) + 1;

      promesasActualizacion.push(
        updateDoc(clienteRef, {
          cuotasAtrasadas: cuotasAtrasadasActualizadas,
          actualizado: fechaActualString,
        }),
      );

      if (!huboCambios) {
        infoClientesActualizado = [...infoClientes];
        huboCambios = true;
      }

      infoClientesActualizado[index] = crearClienteActualizado(cliente, {
        cuotasAtrasadas: cuotasAtrasadasActualizadas,
        actualizado: fechaActualString,
      });
      continue;
    }

    promesasActualizacion.push(
      updateDoc(clienteRef, {
        abono: 0,
        actualizado: fechaActualString,
      }),
    );

    if (!huboCambios) {
      infoClientesActualizado = [...infoClientes];
      huboCambios = true;
    }

    infoClientesActualizado[index] = crearClienteActualizado(cliente, {
      abono: 0,
      actualizado: fechaActualString,
    });
  }

  if (!promesasActualizacion.length) {
    return infoClientes;
  }

  await Promise.all(promesasActualizacion);

  return infoClientesActualizado;
};
